import {
  getBender,
  getShoe,
  getSupportedSizes,
  getVerificationSummary,
  listBenders,
  type BenderModel,
  type ShoeEntry,
} from '@509-bender/bender-library';

export interface BenderSelection {
  benderId: string;
  conduitSize: string;
  material: 'EMT' | 'IMC' | 'RIGID' | 'ALUMINUM';
}

export const MATERIAL_OPTIONS: BenderSelection['material'][] = ['EMT', 'RIGID', 'IMC', 'ALUMINUM'];

export const MATERIAL_LABELS: Record<BenderSelection['material'], string> = {
  EMT: 'EMT',
  RIGID: 'Rigid',
  IMC: 'IMC',
  ALUMINUM: 'Aluminum',
};

export function selectionToShoe(sel: BenderSelection): ShoeEntry | null {
  const shoe = getShoe(sel.benderId, sel.conduitSize, sel.material);
  if (!shoe) return null;
  return shoe;
}

export function normalizeBenderSelection(selection: BenderSelection): BenderSelection {
  const model = getSelectableBender(selection.benderId) ?? getDefaultSelectableBender();
  if (!model) return selection;

  const materials = getSupportedMaterials(model.id);
  const material = materials.includes(selection.material)
    ? selection.material
    : (materials[0] ?? selection.material);
  const sizes = getSupportedSizes(model.id, material);
  const conduitSize = sizes.includes(selection.conduitSize)
    ? selection.conduitSize
    : (sizes[0] ?? selection.conduitSize);

  return {
    benderId: model.id,
    conduitSize,
    material,
  };
}

export function listSelectableBenders(): BenderModel[] {
  return listBenders().filter(isSelectableBender);
}

export function getSupportedMaterials(benderId: string): BenderSelection['material'][] {
  const requestedModel = getBender(benderId);
  const model =
    requestedModel && isSelectableBender(requestedModel)
      ? requestedModel
      : requestedModel
        ? getDefaultSelectableBender()
        : undefined;
  if (!model) return MATERIAL_OPTIONS;
  const available = new Set(model.shoes.map((shoe) => shoe.material));
  const materials = MATERIAL_OPTIONS.filter((material) => available.has(material));
  return materials.length > 0 ? materials : MATERIAL_OPTIONS;
}

export function formatModelOptionLabel(benderId: string): string {
  switch (benderId) {
    case 'greenlee-hand-bender':
      return 'Greenlee Site-Rite';
    case 'klein-hand-bender':
      return 'Klein 51604/51607';
    case 'gardner-bender-hand':
      return 'Gardner Hand Bender';
    case 'greenlee-555':
      return 'Greenlee 555';
    case 'ideal-big-kahuna':
      return 'Ideal Big Kahuna';
    case 'ridgid-600':
      return 'Ridgid 600';
    default:
      return benderId;
  }
}

function getSelectableBender(benderId: string): BenderModel | undefined {
  const model = getBender(benderId);
  if (!model || !isSelectableBender(model)) return undefined;
  return model;
}

function getDefaultSelectableBender(): BenderModel | undefined {
  return listSelectableBenders()[0] ?? listBenders()[0];
}

function isSelectableBender(bender: BenderModel): boolean {
  return getVerificationSummary(bender).status !== 'field-reference';
}
